/**
 * SHARED
 * Cross-cutting code usable by any module. Must not import from any module.
 *
 * ProductSpecModal — the ONE editor for a product's full specification sheet.
 * Used by BOTH the Product Specifications module and System Settings → Product
 * SKUs ("More"). Because one products row maps 1:1 to one product_specs row,
 * this single modal edits both: the SKU parts (flavor / bottle size / pack /
 * tags) write to `products`; everything else writes to `product_specs`. So
 * editing here also edits the SKU shown in System Settings, and vice-versa.
 *
 * Props:
 *   product   the products row to edit ({ id, name, flavor_id, ... }) or null
 *             to add a brand-new product.
 *   onClose() close without further changes.
 *   onSaved() called after each successful save so the opener can refresh.
 */

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import CreatableSelect from "./CreatableSelect.jsx";
import CreatableMultiSelect from "./CreatableMultiSelect.jsx";
import {
  listSizes, createSize,
  listFlavors, createFlavor,
  listPackSizes, createPackSize,
  listProducts, createProduct, updateProduct,
} from "../utils/referenceDataService.js";
import {
  getProductSpec, upsertProductSpec,
  listSpecColumns, createSpecColumn,
  listProductImages, uploadProductImage, updateProductImageTag, deleteProductImage,
} from "../utils/productSpecsService.js";
import { getMyPrimaryAccount, setSpecUnitSystem } from "../utils/accountService.js";
import {
  SPEC_GROUPS, IMAGE_TAGS, UNIT_SYSTEMS, fieldLabel, UPC_PHOTO_TAGS, UPC_TAG_SET,
} from "../utils/productSpecFields.js";

const byName = (a, b) => a.name.localeCompare(b.name);
const buildSkuName = (flavor, size, pack) => [flavor, size, pack].filter(Boolean).join(" / ");

export default function ProductSpecModal({ product, onClose, onSaved }) {
  const [productId, setProductId] = useState(product?.id || null);
  const isNew = !productId;

  // SKU parts
  const [flavors, setFlavors] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [packs, setPacks] = useState([]);
  const [flavorId, setFlavorId] = useState(product?.flavor_id || "");
  const [sizeId, setSizeId] = useState(product?.size_id || "");
  const [packId, setPackId] = useState(product?.pack_size_id || "");
  const [tags, setTags] = useState(product?.tag || []);
  const [allTags, setAllTags] = useState([]);

  // Spec fields + custom columns
  const [std, setStd] = useState({});            // { fieldKey: stringValue }
  const [columns, setColumns] = useState([]);    // custom column defs
  const [customVals, setCustomVals] = useState({});

  // Images
  const [images, setImages] = useState([]);
  const [uploadTag, setUploadTag] = useState("front");

  // Sheet-wide unit system (stored on the account; drives dimension labels)
  const [unitSystem, setUnitSystem] = useState("us");
  const [accountId, setAccountId] = useState(null);
  const [accountRole, setAccountRole] = useState(null);

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [notice, setNotice] = useState(null);

  // New-column inline form
  const [showAddCol, setShowAddCol] = useState(false);
  const [newColLabel, setNewColLabel] = useState("");
  const [newColType, setNewColType] = useState("text");

  async function loadAll() {
    setErr(null);
    try {
      const [f, s, k, cols, prods, acct] = await Promise.all([
        listFlavors(), listSizes(), listPackSizes(), listSpecColumns(), listProducts(), getMyPrimaryAccount(),
      ]);
      setFlavors(f); setSizes(s); setPacks(k); setColumns(cols);
      setAllTags([...new Set(prods.flatMap((p) => p.tag || []))].sort((a, b) => a.localeCompare(b)));
      if (acct) { setUnitSystem(acct.specUnitSystem); setAccountId(acct.accountId); setAccountRole(acct.role); }

      if (productId) {
        const [spec, imgs] = await Promise.all([getProductSpec(productId), listProductImages(productId)]);
        const next = {};
        SPEC_GROUPS.forEach((g) => g.fields.forEach((fl) => { next[fl.key] = spec?.[fl.key] ?? ""; }));
        setStd(next);
        setCustomVals({ ...(spec?.custom || {}) });
        setImages(imgs);
      }
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { loadAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [productId]);

  const nameOf = (list, id) => list.find((x) => String(x.id) === String(id))?.name ?? "";
  const skuName = buildSkuName(nameOf(flavors, flavorId), nameOf(sizes, sizeId), nameOf(packs, packId));

  const onCreateFlavor = async (name) => { const it = await createFlavor({ name }); setFlavors((p) => [...p, it].sort(byName)); return it; };
  const onCreateSize   = async (name) => { const it = await createSize({ name });   setSizes((p) => [...p, it].sort(byName));   return it; };
  const onCreatePack   = async (name) => { const it = await createPackSize({ name }); setPacks((p) => [...p, it].sort(byName));  return it; };

  function setStdField(key, val) { setStd((s) => ({ ...s, [key]: val })); }
  function setCustomField(key, val) { setCustomVals((c) => ({ ...c, [key]: val })); }

  async function handleAddColumn() {
    const label = newColLabel.trim();
    if (!label) return;
    setBusy(true); setErr(null);
    try {
      const col = await createSpecColumn({ label, dataType: newColType });
      setColumns((c) => [...c, col]);
      setNewColLabel(""); setNewColType("text"); setShowAddCol(false);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  // Persist SKU + spec. For a brand-new product, creates the products row first,
  // then switches the modal into edit mode (so photos can be added).
  async function handleSave() {
    if (!flavorId || !sizeId) { setErr("Flavor and bottle size are required."); return; }
    setBusy(true); setErr(null); setNotice(null);
    try {
      let pid = productId;
      if (pid) {
        await updateProduct(pid, { name: skuName, flavorId, sizeId, packSizeId: packId, tag: tags });
      } else {
        const created = await createProduct({ name: skuName, flavorId, sizeId, packSizeId: packId, tag: tags });
        pid = created.id;
      }
      await upsertProductSpec(pid, { ...std, custom: customVals });
      onSaved?.();
      if (!productId) {
        setProductId(pid); // becomes edit mode; effect reloads spec + enables photos
        setNotice("Product created. You can now add photos below.");
      } else {
        onClose?.(); // existing product: a successful save closes the editor
      }
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !productId) return;
    setBusy(true); setErr(null);
    try {
      const img = await uploadProductImage(productId, uploadTag, file);
      setImages((list) => [...list, img]);
    } catch (e2) { setErr(e2.message); }
    finally { setBusy(false); }
  }

  // Sheet-wide unit toggle. Optimistic; reverts on failure (owner/admin only).
  async function changeUnitSystem(u) {
    if (u === unitSystem || !accountId) return;
    const prev = unitSystem;
    setUnitSystem(u);
    setErr(null);
    try { await setSpecUnitSystem(accountId, u); }
    catch (e2) { setUnitSystem(prev); setErr(e2.message); }
  }

  // Upload (or replace) the single UPC photo for a reserved tag.
  async function handleUpcUpload(tag, file) {
    if (!file || !productId) return;
    setBusy(true); setErr(null);
    try {
      const existing = images.find((im) => im.tag === tag) || null;
      const img = await uploadProductImage(productId, tag, file);
      setImages((list) => [...list.filter((im) => im.id !== existing?.id), img]);
      if (existing) await deleteProductImage(existing.id);
    } catch (e2) { setErr(e2.message); }
    finally { setBusy(false); }
  }

  async function handleRetag(id, tag) {
    setImages((list) => list.map((im) => (im.id === id ? { ...im, tag } : im)));
    try { await updateProductImageTag(id, tag); } catch (e2) { setErr(e2.message); }
  }

  async function handleDeleteImage(id) {
    if (!window.confirm("Remove this photo?")) return;
    setBusy(true); setErr(null);
    try { await deleteProductImage(id); setImages((list) => list.filter((im) => im.id !== id)); }
    catch (e2) { setErr(e2.message); }
    finally { setBusy(false); }
  }

  const fieldInputType = (t) => (t === "number" || t === "int" ? "number" : "text");
  const canEditUnits = accountRole === "owner" || accountRole === "admin";

  // Sheet-wide US/metric toggle, shown on the Product Dimensions header.
  const renderUnitToggle = () => (
    <span className="ps-unit-toggle" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
      title="Applies to all dimensions, the shipping case and the pallet — across the whole sheet.">
      <span className="muted" style={{ fontSize: 12 }}>Units</span>
      {UNIT_SYSTEMS.map((u) => (
        <button key={u} type="button"
          className={unitSystem === u ? "btn" : "btn secondary"}
          disabled={busy || !canEditUnits || unitSystem === u}
          onClick={() => changeUnitSystem(u)}
          style={{ padding: "2px 10px", fontSize: 12 }}>
          {u === "us" ? "US (lbs/in)" : "Metric (kg/cm)"}
        </button>
      ))}
    </span>
  );

  // Inline single-photo slot for a UPC field (reserved tag, one image).
  const renderUpcSlot = (tag) => {
    const img = images.find((im) => im.tag === tag) || null;
    const picker = (text) => (
      <label className="link" style={{ cursor: "pointer", fontSize: 12 }}>
        {text}
        <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" hidden disabled={busy}
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) handleUpcUpload(tag, f); }} />
      </label>
    );
    return (
      <div className="ps-upc-photo" style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 8 }}>
        {isNew ? (
          <span className="muted" style={{ fontSize: 12 }}>Save first to add a UPC photo.</span>
        ) : img ? (
          <>
            {img.signedUrl
              ? <img src={img.signedUrl} alt={`${tag} photo`} style={{ height: 38, maxWidth: 120, objectFit: "contain", borderRadius: 4, border: "1px solid var(--border)" }} />
              : <span className="muted" style={{ fontSize: 12 }}>photo</span>}
            {picker("Replace")}
            <button type="button" className="link" style={{ color: "var(--error)", fontSize: 12 }} onClick={() => handleDeleteImage(img.id)}>Remove</button>
          </>
        ) : picker("+ Add UPC photo")}
      </div>
    );
  };

  // Portal to <body> so the overlay escapes the .appshell stacking context
  // (z-index:1) and renders above the sticky top nav instead of behind it.
  return createPortal(
    <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="modal-card ps-modal" role="dialog" aria-modal="true" aria-label="Product specification">
        <div className="ps-modal-head">
          <div>
            <h2 className="modal-title">{isNew ? "Add Product" : "Product Specification"}</h2>
            <p className="ps-sku-name">{skuName || <span className="muted">pick flavor, size &amp; pack</span>}</p>
          </div>
          <button type="button" className="link" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {err && <p className="error">{err}</p>}
        {notice && <p className="ps-notice">{notice}</p>}

        {loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <div className="ps-modal-body">
            {/* ── SKU parts (writes to products) ── */}
            <section className="ps-group">
              <h3 className="ps-group-title">SKU</h3>
              <div className="ps-grid">
                <label className="ps-field">Flavor
                  <CreatableSelect options={flavors} value={flavorId} onChange={setFlavorId} onCreate={onCreateFlavor} placeholder="Select or type flavor…" />
                </label>
                <label className="ps-field">Bottle Size
                  <CreatableSelect options={sizes} value={sizeId} onChange={setSizeId} onCreate={onCreateSize} placeholder="Select or type size…" />
                </label>
                <label className="ps-field">Pack Size
                  <CreatableSelect options={packs} value={packId} onChange={setPackId} onCreate={onCreatePack} placeholder="Select or type pack…" />
                </label>
                <label className="ps-field">Tags
                  <CreatableMultiSelect options={allTags} value={tags} onChange={setTags} placeholder="Select or type tag…" />
                </label>
              </div>
            </section>

            {/* ── Fixed spec groups (writes to product_specs) ── */}
            {SPEC_GROUPS.map((g) => (
              <section className="ps-group" key={g.title}>
                {g.title === "Product Dimensions" ? (
                  <div className="ps-group-head">
                    <h3 className="ps-group-title">{g.title}</h3>
                    {renderUnitToggle()}
                  </div>
                ) : (
                  <h3 className="ps-group-title">{g.title}</h3>
                )}
                <div className="ps-grid">
                  {g.fields.map((fl) => (
                    <label className="ps-field" key={fl.key}>{fieldLabel(fl, unitSystem)}
                      <input
                        type={fieldInputType(fl.type)}
                        step={fl.type === "number" ? "any" : undefined}
                        value={std[fl.key] ?? ""}
                        onChange={(e) => setStdField(fl.key, e.target.value)}
                      />
                      {UPC_PHOTO_TAGS[fl.key] && renderUpcSlot(UPC_PHOTO_TAGS[fl.key])}
                    </label>
                  ))}
                </div>
              </section>
            ))}

            {/* ── Custom columns ── */}
            <section className="ps-group">
              <div className="ps-group-head">
                <h3 className="ps-group-title">Custom Columns</h3>
                <button type="button" className="link" onClick={() => setShowAddCol((v) => !v)}>{showAddCol ? "Cancel" : "+ Add column"}</button>
              </div>
              {showAddCol && (
                <div className="ps-addcol">
                  <input type="text" placeholder="Column name (e.g. Distributor Item #)" value={newColLabel} onChange={(e) => setNewColLabel(e.target.value)} />
                  <select value={newColType} onChange={(e) => setNewColType(e.target.value)} style={{ width: "auto" }}>
                    <option value="text">Text</option>
                    <option value="number">Number</option>
                  </select>
                  <button className="btn" type="button" disabled={busy || !newColLabel.trim()} onClick={handleAddColumn}>Add</button>
                </div>
              )}
              {columns.length === 0 ? (
                <p className="muted" style={{ margin: "4px 0 0" }}>No custom columns yet — add your own parameters above.</p>
              ) : (
                <div className="ps-grid">
                  {columns.map((col) => (
                    <label className="ps-field" key={col.id}>{col.label}
                      <input
                        type={col.data_type === "number" ? "number" : "text"}
                        step={col.data_type === "number" ? "any" : undefined}
                        value={customVals[col.key] ?? ""}
                        onChange={(e) => setCustomField(col.key, e.target.value)}
                      />
                    </label>
                  ))}
                </div>
              )}
            </section>

            {/* ── Tagged photos ── */}
            <section className="ps-group">
              <h3 className="ps-group-title">Photos</h3>
              {isNew ? (
                <p className="muted" style={{ margin: 0 }}>Save the product first to add photos.</p>
              ) : (
                <>
                  <div className="ps-upload">
                    <select value={uploadTag} onChange={(e) => setUploadTag(e.target.value)} style={{ width: "auto" }}>
                      {IMAGE_TAGS.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <label className="ps-uploadbtn">
                      Upload photo
                      <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={handleUpload} disabled={busy} hidden />
                    </label>
                  </div>
                  {images.filter((im) => !UPC_TAG_SET.has(im.tag)).length === 0 ? (
                    <p className="muted" style={{ margin: "8px 0 0" }}>No photos yet.</p>
                  ) : (
                    <div className="ps-images">
                      {images.filter((im) => !UPC_TAG_SET.has(im.tag)).map((im) => (
                        <div className="ps-image" key={im.id}>
                          {im.signedUrl
                            ? <img src={im.signedUrl} alt={im.tag} />
                            : <div className="ps-image-missing">image</div>}
                          <select value={IMAGE_TAGS.includes(im.tag) ? im.tag : "other"} onChange={(e) => handleRetag(im.id, e.target.value)}>
                            {IMAGE_TAGS.map((t) => <option key={t} value={t}>{t}</option>)}
                          </select>
                          <button type="button" className="link" style={{ color: "var(--error)" }} onClick={() => handleDeleteImage(im.id)}>Delete</button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </section>
          </div>
        )}

        <div className="modal-actions">
          <button type="button" className="secondary" onClick={onClose}>{isNew ? "Cancel" : "Close"}</button>
          <button type="button" className="btn" disabled={busy || loading || !flavorId || !sizeId} onClick={handleSave}>
            {busy ? "Saving…" : isNew ? "Create Product" : "Save"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
